import {
  calculateConversionRate,
  calculateTextingRatio,
} from '@common/utils/ofStatistics'
import type { ChartData } from 'chart.js'
import { format, parseISO } from 'date-fns'

import type { Creator } from '@/modules/creators/types.ts'

import type { DetailedComparisonType, StatisticsData } from './types'

export const handleLabels = (
  statistics: StatisticsData,
  isOneDay?: boolean,
  modular = 1,
) => {
  return Object.values(statistics.data)
    .filter((f, i) => i % modular === 0)
    .map((d) => (isOneDay ? format(parseISO(d.key), 'h aaa') : d.key))
}

export const mapRevenueData = (
  statistics: StatisticsData | undefined,
  isBlue?: boolean,
  isSimple?: boolean,
  isOneDay?: boolean,
) => {
  if (!statistics) return

  const length = Object.values(statistics.data).length
  const modular = isSimple ? Math.round(length / 30 + 1) : 1

  const chartData: ChartData<'line'> = {
    labels: handleLabels(statistics, isOneDay, modular),
    datasets: [
      {
        data: Object.values(statistics.data)
          .filter((f, i) => i % modular === 0)
          .map((d) => d.totalRevenue),
        borderColor: isBlue ? '#1E84E3' : '#0BC9CD',
      },
    ],
  }

  return chartData
}

export const mapSubscriptionsData = (
  statistics: StatisticsData | undefined,
  isOneDay?: boolean,
) => {
  if (!statistics) return

  const res: ChartData<'line'> = {
    labels: handleLabels(statistics, isOneDay),
    datasets: [
      {
        data: Object.values(statistics.data).map((d) => d.newSubscriptions),
        borderColor: '#1E84E3',
      },
    ],
  }

  return res
}

export const mapNewSubscriptionsData = (
  statistics: StatisticsData | undefined,
  isBlue?: boolean,
  isSimple?: boolean,
  isOneDay?: boolean,
) => {
  if (!statistics) return

  const length = Object.values(statistics.data).length
  const modular = isSimple ? Math.round(length / 15 + 1) : 1

  const res: ChartData<'line'> = {
    labels: handleLabels(statistics, isOneDay, modular),
    datasets: [
      {
        data: Object.values(statistics.data)
          .filter((f, i) => i % modular === 0)
          .map((d) => Number(d.newSubscriptions.toFixed(0))),
        borderColor: isBlue ? '#1E84E3' : '#0BC9CD',
      },
    ],
  }

  return res
}

export const mapTotalPurchasesData = (
  statistics: StatisticsData | undefined,
  isSimple?: boolean,
  isOneDay?: boolean,
) => {
  if (!statistics) return

  const length = Object.values(statistics.data).length
  const modular = isSimple ? Math.round(length / 15 + 1) : 1

  const res: ChartData<'line'> = {
    labels: handleLabels(statistics, isOneDay, modular),
    datasets: [
      {
        data: Object.values(statistics.data)
          .filter((f, i) => i % modular === 0)
          .map((d) => d.totalPurchases),
        borderColor: '#0BC9CD',
      },
    ],
  }

  return res
}

export const mapDetailedComparisonData = (
  statistics: StatisticsData,
  creator: Creator,
  useAllSubsRevenue = false,
): DetailedComparisonType => {
  const hasProfessional = creator.plan.startsWith('professional')

  return {
    creatorId: creator.id,
    creator: creator.name,
    creatorUserName: creator.userName,
    totalRevenue: statistics.total.totalRevenue,
    newSubs: statistics.total.newSubscriptions,
    newSubscriptionsRevenue: statistics.total.newSubscriptionsRevenue,
    recurringSubscriptionsRevenue:
      statistics.total.recurringSubscriptionsRevenue,
    totalSubscriptionsRevenue: statistics.total.totalSubscriptionsRevenue,
    tipsRevenue: statistics.total.tipsRevenue,
    messageRevenue: statistics.total.messageRevenue,
    averageFanSpend: statistics.total.averageFanSpend,
    spendingFansCount: statistics.total.spendingFansCount,
    textingRatio: calculateTextingRatio(
      useAllSubsRevenue
        ? statistics.total.totalSubscriptionsRevenue
        : statistics.total.newSubscriptionsRevenue,
      statistics.total.messageRevenue,
      statistics.total.tipsRevenue,
    ),
    openChats: hasProfessional ? statistics.total.openChatCount : '/',
    sellingChats: hasProfessional ? statistics.total.sellingChatCount : '/',
    linkClicks: statistics.total.linkClicks,
    conversionRate: calculateConversionRate(
      statistics.total.linkClicks,
      statistics.total.newSubscriptions,
    ),
  }
}
